import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './MediaPricelistDetail.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/** Sloupce tabulek téhle obrazovky — klíče, pod kterými se ukládají šířky. */
const SLOUPCE_PROJEKTY = ['nazev', 'datumVydani', 'ucetniDatum', 'stranky', 'gdrive', 'cenik', 'akce'];
const SLOUPCE_CENIKY = ['nazev', 'id', 'mutace', 'stav', 'poznamka', 'akce'];
const SLOUPCE_POLOZKY = [
  'vyber', 'spolecnost', 'datum', 'objednavka', 'polozkyFaktury', 'interniPoznamka',
  'poznamkaPolozky', 'polozkaCeniku', 'netCena', 'obchodnik', 'id', 'medium',
];

function MediaPricelistDetail({ mediaId, openTab, openProjectId }) {
  const [medium, setMedium] = useState({});
  const [projects, setProjects] = useState([]);
  const [ratecards, setRatecards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const projektySirky = useColumnWidths('media.projects', SLOUPCE_PROJEKTY);
  const cenikySirky = useColumnWidths('media.ratecards', SLOUPCE_CENIKY);
  const polozkySirky = useColumnWidths('project.bookingItems', SLOUPCE_POLOZKY);

  const [activeTab, setActiveTab] = useState('projekty');
  const [projectSearch, setProjectSearch] = useState('');

  const [mutations, setMutations] = useState([]);
  const [mutationsLoading, setMutationsLoading] = useState(false);
  const [mutationSearch, setMutationSearch] = useState('');

  // CRUD modals
  const [mutationModal, setMutationModal] = useState(null); // { mode:'create'|'edit', data }
  const [deleteMutationConfirm, setDeleteMutationConfirm] = useState(null);
  const [savingMutation, setSavingMutation] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getMedium(mediaId);
      if (result.data) {
        setMedium(result.data);
        setProjects(result.data.projects || []);
        setRatecards(result.data.ratecards || []);
      }
    } catch (err) {
      console.error('Failed to fetch medium detail:', err);
    } finally {
      setLoading(false);
    }
  }, [mediaId]);

  useEffect(() => {
    if (mediaId) {
      fetchDetail();
    }
  }, [mediaId, fetchDetail]);

  const fetchMutations = useCallback(() => {
    if (!mediaId) return Promise.resolve();
    setMutationsLoading(true);
    return api.getMediumMutations(mediaId)
      .then(res => setMutations(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMutations([]))
      .finally(() => setMutationsLoading(false));
  }, [mediaId]);

  useEffect(() => { fetchMutations(); }, [fetchMutations]);

  const filteredMutations = useMemo(() => {
    if (!mutationSearch) return mutations;
    const q = mutationSearch.toLowerCase();
    return mutations.filter(m => (m.name || '').toLowerCase().includes(q));
  }, [mutations, mutationSearch]);

  // Listen for refresh events (e.g. after project/ratecard save)
  useEffect(() => {
    const handler = (e) => {
      const targetMediaId = e.detail?.mediaId;
      if (!targetMediaId || String(targetMediaId) === String(mediaId)) {
        fetchDetail();
      }
    };
    window.addEventListener('media:refresh', handler);
    return () => window.removeEventListener('media:refresh', handler);
  }, [mediaId, fetchDetail]);

  // Find the active ratecard
  const activeRatecard = Array.isArray(ratecards) ? ratecards.find(rc => rc.is_available) || null : null;

  // Filter projects by search
  const filteredProjects = useMemo(() => {
    if (!projectSearch) return projects;
    const term = projectSearch.toLowerCase();
    return projects.filter(p =>
      (p.name && p.name.toLowerCase().includes(term)) ||
      (p.date && p.date.includes(term))
    );
  }, [projects, projectSearch]);

  // Format ISO date string to dd.mm.yyyy
  // Datum se čte tak, jak je zapsané, ne přes `new Date()`. Čtení v UTC posouvalo
  // datum o den zpátky, kdykoliv přišlo s časovou zónou: pražská půlnoc je v UTC
  // 22:00 (v zimě 23:00) předchozího dne, takže vydání 9. 10. vycházelo jako 8. 10.
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr));
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  // Extract GDrive link from note field
  const getGdriveLink = (note) => {
    if (!note) return null;
    const urlMatch = note.match(/https?:\/\/[^\s]+/);
    return urlMatch ? urlMatch[0] : null;
  };

  const handleEditMedium = () => {
    openTab(
      `media-edit-${mediaId}`,
      `Upravit: ${medium.name || 'Médium'}`,
      `/media/${mediaId}/edit`,
      'MediaForm',
      { mediaId: mediaId }
    );
  };

  const handleAddProject = () => {
    openTab(
      `project-new-${mediaId}`,
      'Nový projekt',
      `/media/${mediaId}/project/new`,
      'ProjectForm',
      { mediaId, mediaName: medium.name }
    );
  };

  const handleEditProject = (project) => {
    openTab(
      `project-edit-${project.id}`,
      `Upravit: ${project.name}`,
      `/media/${mediaId}/project/${project.id}/edit`,
      'ProjectForm',
      { mediaId, mediaName: medium.name, projectId: project.id }
    );
  };

  // Project detail modal state
  const [detailModalProject, setDetailModalProject] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBookingItems, setDetailBookingItems] = useState([]);
  const [selectedBookingItems, setSelectedBookingItems] = useState(new Set());

  const toggleBookingItemSelection = (itemId) => {
    setSelectedBookingItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const toggleAllBookingItems = () => {
    if (selectedBookingItems.size === detailBookingItems.length) {
      setSelectedBookingItems(new Set());
    } else {
      setSelectedBookingItems(new Set(detailBookingItems.map(i => i.id)));
    }
  };

  const handleShowProjectDetail = async (project) => {
    setDetailModalProject(project);
    setDetailLoading(true);
    setDetailBookingItems([]);
    setSelectedBookingItems(new Set());
    try {
      const result = await api.getProjectBookingItems(project.id);
      if (result.data?.items) {
        setDetailBookingItems(result.data.items);
      }
    } catch (err) {
      console.error('Failed to fetch project booking items:', err);
      showToast('Nepodařilo se načíst detail projektu.', 'danger');
    } finally {
      setDetailLoading(false);
    }
  };

  // Auto-open project detail modal when openProjectId changes (e.g. from GlobalSearch)
  const handledProjectIdRef = useRef(null);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  useEffect(() => {
    if (!openProjectId || handledProjectIdRef.current === openProjectId) return;
    handledProjectIdRef.current = openProjectId;

    // Wait for projects to load, then open the modal
    const tryOpen = () => {
      const p = projectsRef.current.find(
        pr => pr.id === openProjectId || String(pr.id) === String(openProjectId)
      );
      if (p) {
        handleShowProjectDetail(p);
      } else if (!loading) {
        handleShowProjectDetail({ id: openProjectId, name: `Projekt #${openProjectId}` });
      }
    };

    if (!loading && projectsRef.current.length > 0) {
      tryOpen();
    } else {
      // Projects not loaded yet — wait a bit
      const timer = setTimeout(tryOpen, 500);
      return () => clearTimeout(timer);
    }
  }, [openProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatPrice = (price, currency) => {
    if (price === null || price === undefined) return '—';
    const formatted = Number(price).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${formatted} ${currency}` : formatted;
  };

  // Create invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('');
  const [invoiceTypesLoading, setInvoiceTypesLoading] = useState(false);
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [separateCompanies, setSeparateCompanies] = useState(new Set());

  const handleOpenInvoiceModal = async () => {
    setShowInvoiceModal(true);
    setSelectedInvoiceType('');
    setSeparateCompanies(new Set());
    setInvoiceTypesLoading(true);
    try {
      const result = await api.getInvoiceEnums();
      if (result.data?.types) {
        setInvoiceTypes(result.data.types);
        if (result.data.types.length > 0) {
          setSelectedInvoiceType(result.data.types[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch invoice types:', err);
    } finally {
      setInvoiceTypesLoading(false);
    }
  };

  const toggleSeparateCompany = (companyName) => {
    setSeparateCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyName)) {
        next.delete(companyName);
      } else {
        next.add(companyName);
      }
      return next;
    });
  };

  // Group selected booking items by company for invoice preview
  const invoiceGroups = useMemo(() => {
    if (!showInvoiceModal || selectedBookingItems.size === 0) return [];
    const selected = detailBookingItems.filter(i => selectedBookingItems.has(i.id));
    const groups = {};
    selected.forEach(item => {
      const key = item.company_name || '—';
      if (!groups[key]) {
        groups[key] = {
          company_name: item.company_name || '—',
          currency: item.currency || 'CZK',
          items: [],
          total_net: 0,
          booking_item_ids: [],
          internal_notes: new Set(),
          project_names: new Set(),
          issue_dates: new Set(),
        };
      }
      groups[key].items.push(item);
      groups[key].total_net += item.net_net_price || 0;
      groups[key].booking_item_ids.push(item.id);
      if (item.internal_note) {
        groups[key].internal_notes.add(item.internal_note);
      }
      if (detailModalProject?.name) {
        groups[key].project_names.add(detailModalProject.name);
      }
      // Datum vydání — z položky, fallback datum projektu
      const issueDate = item.date || detailModalProject?.date;
      if (issueDate) {
        groups[key].issue_dates.add(issueDate);
      }
    });
    // Convert Sets to arrays for rendering
    return Object.values(groups).map(g => ({
      ...g,
      internal_notes: [...g.internal_notes],
      project_names: [...g.project_names],
      issue_dates: [...g.issue_dates].sort(),
    }));
  }, [showInvoiceModal, selectedBookingItems, detailBookingItems, detailModalProject]);

  // Handle invoice creation
  const handleCreateInvoices = async () => {
    if (invoiceCreating) return;
    setInvoiceCreating(true);
    try {
      const allBookingItemIds = invoiceGroups.flatMap(g => g.booking_item_ids);
      const result = await api.createInvoices({
        bookingItemIds: allBookingItemIds,
        invoiceType: selectedInvoiceType,
        separateCompanies: [...separateCompanies],
      });

      if (result.status === 'ok' && result.data) {
        const count = result.data.count || 0;
        const warnings = result.data.aresWarnings || [];
        let msg = `Úspěšně vytvořeno ${count} ${count === 1 ? 'faktura' : count < 5 ? 'faktury' : 'faktur'}.`;
        if (warnings.length > 0) {
          msg += '\n⚠ ' + warnings.join('\n⚠ ');
          showToast(msg, 'warning');
        } else {
          showToast(msg, 'success');
        }
        window.dispatchEvent(new CustomEvent('invoices:refresh'));
        setShowInvoiceModal(false);
        setSelectedBookingItems(new Set());
        // Refresh booking items to show updated invoice references
        if (detailModalProject) {
          handleShowProjectDetail(detailModalProject);
        }
      } else {
        showToast(result.message || 'Chyba při vytváření faktur.', 'danger');
      }
    } catch (err) {
      console.error('Failed to create invoices:', err);
      showToast(err.message || 'Nepodařilo se vytvořit faktury.', 'danger');
    } finally {
      setInvoiceCreating(false);
    }
  };

  // Delete project state
  const [deleteModalProject, setDeleteModalProject] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteProject = async () => {
    if (!deleteModalProject) return;
    setDeleting(true);
    try {
      await api.deleteProject(deleteModalProject.id);
      setDeleteModalProject(null);
      showToast('Projekt byl smazán.', 'success');
      fetchDetail(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete project:', err);
      showToast('Nepodařilo se smazat projekt.', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddRatecard = () => {
    openTab(
      `ratecard-new-${mediaId}`,
      'Nový ceník',
      `/media/${mediaId}/ratecard/new`,
      'RatecardForm',
      { mediaId, mediaName: medium.name }
    );
  };

  const handleEditRatecard = (ratecard) => {
    openTab(
      `ratecard-edit-${ratecard.id}`,
      `Upravit: ${ratecard.name}`,
      `/media/${mediaId}/ratecard/${ratecard.id}/edit`,
      'RatecardForm',
      { mediaId, mediaName: medium.name, ratecardId: ratecard.id }
    );
  };

  // === MUTATIONS ===
  const openCreateMutation = () => setMutationModal({
    mode: 'create',
    data: { name: '', cost_center: '', available: true, business_only: false, parent_id: '', description: '' },
  });
  const openEditMutation = (m) => setMutationModal({
    mode: 'edit',
    data: {
      id: m.id,
      name: m.name || '',
      cost_center: m.cost_center || '',
      available: !!m.available,
      business_only: !!m.business_only,
      parent_id: m.parent_id ?? '',
      description: m.description || '',
    },
  });
  const handleSaveMutation = async () => {
    if (!mutationModal) return;
    const d = mutationModal.data;
    if (!d.name.trim()) { alert('Název mutace je povinný.'); return; }
    setSavingMutation(true);
    try {
      const payload = {
        name: d.name.trim(),
        cost_center: d.cost_center || '',
        available: d.available ? 1 : 0,
        business_only: d.business_only ? 1 : 0,
        parent_id: d.parent_id === '' ? null : Number(d.parent_id),
        description: d.description || '',
      };
      if (mutationModal.mode === 'create') {
        await api.createMutation(mediaId, payload);
      } else {
        await api.updateMutation(d.id, payload);
      }
      await fetchMutations();
      setMutationModal(null);
    } catch (e) {
      console.error(e);
      alert('Uložení se nezdařilo.');
    } finally {
      setSavingMutation(false);
    }
  };
  const handleDeleteMutation = async () => {
    if (!deleteMutationConfirm) return;
    try {
      await api.deleteMutation(deleteMutationConfirm.id);
      await fetchMutations();
      setDeleteMutationConfirm(null);
    } catch (e) {
      console.error(e);
      alert('Mazání se nezdařilo (mutace může být použita v ceníku).');
    }
  };

  const handleOpenPricelistItems = (ratecard) => {
    openTab(
      `pricelist-items-${ratecard.id}`,
      `Ceník: ${ratecard.name}`,
      `/media-pricelist/${mediaId}/pricelist/${ratecard.id}`,
      'PricelistItems',
      {
        pricelistId: String(ratecard.id),
        pricelistName: ratecard.name,
        mediaName: medium.name,
        mediaId: mediaId,
      }
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Načítání...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mpd-layout">
      {/* SIDEBAR */}
      <div className="mpd-sidebar">
        <div className="card card-tile mpd-mutations-card">
          <div className="card-header d-flex align-items-center gap-2">
            <strong><i className="bi bi-collection me-2"></i>Média / mutace</strong>
            <input
              type="text"
              className="form-control form-control-sm ms-auto mpd-mut-search"
              placeholder="Hledat…"
              value={mutationSearch}
              onChange={(e) => setMutationSearch(e.target.value)}
            />
          </div>
          <div className="card-body">
            <div className="mpd-mut-title small mb-2">
              <span className="text-muted">Vybraný titul:</span>{' '}
              <strong>{medium.name || '—'}</strong>
            </div>

            <div className="mpd-mut-list">
              {mutationsLoading ? (
                <div className="text-muted small py-2">Načítám…</div>
              ) : filteredMutations.length === 0 ? (
                <div className="text-muted small py-2">
                  {mutationSearch ? 'Žádné mutace neodpovídají hledání.' : 'Pro tento titul nejsou definované mutace.'}
                </div>
              ) : (
                filteredMutations.map(m => (
                  <div key={m.id} className="mpd-mut-item">
                    <div className="mpd-mut-name">
                      {medium.name ? `${medium.name} – ${m.name}` : m.name}
                    </div>
                    {medium.publisher_name && (
                      <div className="mpd-mut-firma">Firma: {medium.publisher_name}</div>
                    )}
                  </div>
                ))
              )}
            </div>

            <button
              className="btn btn-outline-primary btn-sm w-100 mt-3"
              onClick={handleEditMedium}
            >
              <i className="bi bi-pencil me-1"></i>Upravit médium
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="mpd-main-content">
        {/* Breadcrumb */}
        <div className="mpd-breadcrumb">
          <span className="small-muted">Kontext</span>
          <span
            className="mpd-breadcrumb-link"
            onClick={() => openTab('media-pricelist', 'Média a ceníky', '/media-pricelist', 'MediaPricelist')}
            role="button"
          >
            Média a ceníky
          </span>
          <i className="bi bi-arrow-right small-muted"></i>
          <strong>{medium.name || '—'}</strong>
          {(medium.type_label || medium.type) && (
            <span className="badge bg-info ms-2">{medium.type_label || medium.type}</span>
          )}
          {medium.is_available ? (
            <span className="badge bg-success ms-1">Aktivní</span>
          ) : (
            <span className="badge bg-secondary ms-1">Neaktivní</span>
          )}
        </div>

        {/* Content Card */}
        <div className="card card-tile">
          <div className="card-header">
            <div className="mpd-tabs-nav">
              <button
                className={`mpd-tab-link ${activeTab === 'projekty' ? 'active' : ''}`}
                onClick={() => setActiveTab('projekty')}
              >
                Projekty ({projects.length})
              </button>
              <button
                className={`mpd-tab-link ${activeTab === 'ceniky' ? 'active' : ''}`}
                onClick={() => setActiveTab('ceniky')}
              >
                Ceníky ({ratecards.length})
              </button>
              <button
                className={`mpd-tab-link ${activeTab === 'mutace' ? 'active' : ''}`}
                onClick={() => setActiveTab('mutace')}
              >
                Mutace ({mutations.length})
              </button>
            </div>
          </div>

          <div className="card-body">
            {activeTab === 'projekty' && (
              <>
                <div className="mpd-filter-row">
                  <div style={{ flex: 1, maxWidth: '400px' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Hledat projekt…"
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                    />
                  </div>
                  <div className="ms-auto d-flex align-items-center gap-3">
                    <small className="text-muted">
                      {filteredProjects.length} z {projects.length} projektů
                      <ColumnWidthsReset maUlozene={projektySirky.maUlozene} vychozi={projektySirky.vychozi} className="ms-2" />
                    </small>
                    <button className="btn btn-sm btn-primary" onClick={handleAddProject}>
                      <i className="bi bi-plus-lg me-1"></i>Přidat projekt
                    </button>
                  </div>
                </div>

                <div className="table-responsive grid-wrap" ref={projektySirky.wrapRef}>
                  <table className="table table-sm align-middle mb-0 grid-resizable" style={projektySirky.tableStyle}>
                    {projektySirky.colgroup && (
                      <colgroup>
                        {projektySirky.colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                      </colgroup>
                    )}
                    <thead className="table-light">
                      <tr>
                        <th>Název projektu<span {...projektySirky.resizerProps('nazev')} /></th>
                        <th>Datum vydání<span {...projektySirky.resizerProps('datumVydani')} /></th>
                        <th>Účetní datum<span {...projektySirky.resizerProps('ucetniDatum')} /></th>
                        <th>Stránky<span {...projektySirky.resizerProps('stranky')} /></th>
                        <th>GDrive<span {...projektySirky.resizerProps('gdrive')} /></th>
                        <th>Ceník (projekt)<span {...projektySirky.resizerProps('cenik')} /></th>
                        <th><span {...projektySirky.resizerProps('akce')} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProjects.map(p => {
                        const gdriveLink = getGdriveLink(p.note);
                        return (
                          <tr key={p.id}>
                            <td>
                              <div
                                style={{ fontWeight: 600, cursor: 'pointer' }}
                                className="mpd-project-name-link"
                                role="button"
                                onClick={() => handleShowProjectDetail(p)}
                              >
                                {p.name}
                              </div>
                              <span className="text-muted" style={{ fontSize: '0.85rem' }}>ID: {p.external_id || p.id}</span>
                            </td>
                            <td>{formatDate(p.date)}</td>
                            <td>{formatDate(p.accounting_date)}</td>
                            <td>{p.pages && p.pages !== '0.0' ? p.pages : '—'}</td>
                            <td>
                              {gdriveLink ? (
                                <a href={gdriveLink} className="text-decoration-none" target="_blank" rel="noopener noreferrer">
                                  <i className="bi bi-box-arrow-up-right me-1"></i>odkaz
                                </a>
                              ) : '—'}
                            </td>
                            <td>
                              {activeRatecard ? (
                                <span
                                  className="badge mpd-badge-pricelist-active mpd-badge-clickable"
                                  role="button"
                                  onClick={() => handleOpenPricelistItems(activeRatecard)}
                                >
                                  {activeRatecard.name}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex gap-1 justify-content-end">
                                <button
                                  className="btn btn-sm btn-outline-danger btn-action"
                                  title="Smazat projekt"
                                  onClick={() => setDeleteModalProject(p)}
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary btn-action"
                                  title="Upravit projekt"
                                  onClick={() => handleEditProject(p)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-primary btn-action"
                                  title="Detail projektu"
                                  onClick={() => handleShowProjectDetail(p)}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredProjects.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">Žádné projekty</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'ceniky' && (
              <>
                <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                  <ColumnWidthsReset maUlozene={cenikySirky.maUlozene} vychozi={cenikySirky.vychozi} />
                  <button className="btn btn-sm btn-primary" onClick={handleAddRatecard}>
                    <i className="bi bi-plus-lg me-1"></i>Přidat ceník
                  </button>
                </div>
                <div className="table-responsive grid-wrap" ref={cenikySirky.wrapRef}>
                  <table className="table table-sm mpd-table-pricelist align-middle mb-0 grid-resizable" style={cenikySirky.tableStyle}>
                    {cenikySirky.colgroup && (
                      <colgroup>
                        {cenikySirky.colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                      </colgroup>
                    )}
                    <thead className="table-light">
                      <tr>
                        <th>Název ceníku<span {...cenikySirky.resizerProps('nazev')} /></th>
                        <th>ID<span {...cenikySirky.resizerProps('id')} /></th>
                        <th>Mutace<span {...cenikySirky.resizerProps('mutace')} /></th>
                        <th>Stav<span {...cenikySirky.resizerProps('stav')} /></th>
                        <th>Poznámka<span {...cenikySirky.resizerProps('poznamka')} /></th>
                        <th className="text-end">Akce<span {...cenikySirky.resizerProps('akce')} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ratecards.map(rc => (
                        <tr key={rc.id}>
                          <td
                            className="mpd-pricelist-name"
                            role="button"
                            onClick={() => handleOpenPricelistItems(rc)}
                          >
                            {rc.name}
                          </td>
                          <td className="text-muted" style={{ fontSize: '0.85rem' }}>{rc.external_id || rc.id}</td>
                          <td>{rc.mutation_name || '—'}</td>
                          <td>
                            {rc.is_available ? (
                              <span className="badge bg-success">Aktivní</span>
                            ) : (
                              <span className="badge bg-secondary">Neaktivní</span>
                            )}
                          </td>
                          <td>{rc.note || '—'}</td>
                          <td>
                            <div className="mpd-action-buttons">
                              <button
                                className="btn btn-sm btn-outline-secondary btn-action me-1"
                                title="Upravit ceník"
                                onClick={() => handleEditRatecard(rc)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary btn-action"
                                title="Otevřít položky"
                                onClick={() => handleOpenPricelistItems(rc)}
                              >
                                <i className="bi bi-list-ul"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {ratecards.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">Žádné ceníky</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'mutace' && (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <button className="btn btn-sm btn-primary" onClick={openCreateMutation}>
                    <i className="bi bi-plus-lg me-1"></i>Nová mutace
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Název</th>
                        <th>Nadřazené</th>
                        <th>Dostupná</th>
                        <th className="text-end">Akce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mutationsLoading ? (
                        <tr><td colSpan="4" className="text-center text-muted py-4">Načítám…</td></tr>
                      ) : mutations.length === 0 ? (
                        <tr><td colSpan="4" className="text-center text-muted py-4">Žádné mutace</td></tr>
                      ) : mutations.map(m => (
                        <tr key={m.id}>
                          <td>{m.name}</td>
                          <td>{m.parent_name || '—'}</td>
                          <td>{m.available
                            ? <span className="badge bg-success">Ano</span>
                            : <span className="badge bg-secondary">Ne</span>}
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-secondary btn-action me-1"
                              title="Upravit" onClick={() => openEditMutation(m)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger btn-action"
                              title="Smazat" onClick={() => setDeleteMutationConfirm(m)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* Mutation create/edit modal */}
      <Modal show={!!mutationModal} onHide={() => !savingMutation && setMutationModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {mutationModal?.mode === 'create' ? 'Nová mutace' : `Upravit: ${mutationModal?.data?.name || ''}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mutationModal && (
            <>
              <div className="mb-2">
                <label className="form-label small">Název *</label>
                <input type="text" className="form-control form-control-sm"
                  value={mutationModal.data.name}
                  onChange={e => setMutationModal(m => ({ ...m, data: { ...m.data, name: e.target.value } }))}
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Nadřazené</label>
                <select className="form-select form-select-sm"
                  value={mutationModal.data.parent_id}
                  onChange={e => setMutationModal(m => ({ ...m, data: { ...m.data, parent_id: e.target.value } }))}
                >
                  <option value="">— žádná —</option>
                  {mutations
                    .filter(m => m.id !== mutationModal.data.id) // zamezíme self-ref
                    .map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div className="form-check mb-2">
                <input type="checkbox" className="form-check-input"
                  id="mut-available"
                  checked={mutationModal.data.available}
                  onChange={e => setMutationModal(m => ({ ...m, data: { ...m.data, available: e.target.checked } }))}
                />
                <label htmlFor="mut-available" className="form-check-label">Dostupná</label>
              </div>
              <div>
                <label className="form-label small">Popis</label>
                <textarea className="form-control form-control-sm" rows={2}
                  value={mutationModal.data.description}
                  onChange={e => setMutationModal(m => ({ ...m, data: { ...m.data, description: e.target.value } }))}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setMutationModal(null)} disabled={savingMutation}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveMutation} disabled={savingMutation}>
            {savingMutation ? 'Ukládám…' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mutation delete confirm */}
      <Modal show={!!deleteMutationConfirm} onHide={() => setDeleteMutationConfirm(null)} centered>
        <Modal.Header closeButton><Modal.Title>Smazat mutaci</Modal.Title></Modal.Header>
        <Modal.Body>
          Opravdu chcete smazat mutaci <strong>{deleteMutationConfirm?.name}</strong>?
          <div className="text-muted small mt-2">
            Pokud je mutace přiřazená k některému ceníku, mazání selže — nejdřív ji odpojte.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteMutationConfirm(null)}>Zrušit</Button>
          <Button variant="danger" onClick={handleDeleteMutation}>Smazat</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Project Confirmation Modal */}
      <Modal show={!!deleteModalProject} onHide={() => setDeleteModalProject(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Smazat projekt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Opravdu chcete smazat projekt <strong>{deleteModalProject?.name}</strong>?
          <br />
          <span className="text-muted">Tato akce je nevratná.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteModalProject(null)} disabled={deleting}>
            Zrušit
          </Button>
          <Button variant="danger" onClick={handleDeleteProject} disabled={deleting}>
            {deleting ? 'Mažu…' : 'Smazat'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Detail Modal */}
      <Modal
        show={!!detailModalProject}
        onHide={() => setDetailModalProject(null)}
        size="xl"
        dialogClassName="mpd-project-detail-modal"
        centered
        // Dialog „Vytvořit fakturu" je vlastní vrstva mimo tento modal — bez vypnutí
        // by si modal bral fokus zpět a select v dialogu by se hned zavřel.
        enforceFocus={!showInvoiceModal}
        keyboard={!showInvoiceModal}
      >
        <Modal.Header closeButton>
          <div className="d-flex align-items-center justify-content-between w-100 me-3">
            <Modal.Title>
              <i className="bi bi-eye me-2"></i>
              Detail projektu: {detailModalProject?.name}
            </Modal.Title>
            {selectedBookingItems.size > 0 && (
              <Button variant="primary" size="sm" onClick={handleOpenInvoiceModal}>
                <i className="bi bi-receipt me-1"></i>
                Vytvořit fakturu ({selectedBookingItems.size})
              </Button>
            )}
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {detailLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Načítání...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="px-3 pt-3 pb-2 d-flex align-items-center justify-content-between">
                <small className="text-muted">
                  Nalezené záznamy: {detailBookingItems.length}
                </small>
                <ColumnWidthsReset maUlozene={polozkySirky.maUlozene} vychozi={polozkySirky.vychozi} />
              </div>
              <div className="table-responsive grid-wrap" ref={polozkySirky.wrapRef}>
                <table className="table table-sm table-hover align-middle mb-0 mpd-project-detail-table grid-resizable" style={polozkySirky.tableStyle}>
                  {polozkySirky.colgroup && (
                    <colgroup>
                      {polozkySirky.colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                    </colgroup>
                  )}
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={detailBookingItems.length > 0 && selectedBookingItems.size === detailBookingItems.length}
                          onChange={toggleAllBookingItems}
                        />
                        <span {...polozkySirky.resizerProps('vyber')} />
                      </th>
                      <th>Název společnosti<span {...polozkySirky.resizerProps('spolecnost')} /></th>
                      <th>Datum<span {...polozkySirky.resizerProps('datum')} /></th>
                      <th>Objednávka<span {...polozkySirky.resizerProps('objednavka')} /></th>
                      <th>Položky faktury<span {...polozkySirky.resizerProps('polozkyFaktury')} /></th>
                      <th>Interní poznámka<span {...polozkySirky.resizerProps('interniPoznamka')} /></th>
                      <th>Poznámka položky<span {...polozkySirky.resizerProps('poznamkaPolozky')} /></th>
                      <th>Položka ceníku<span {...polozkySirky.resizerProps('polozkaCeniku')} /></th>
                      <th className="text-end">Net cena<span {...polozkySirky.resizerProps('netCena')} /></th>
                      <th>Obchodník<span {...polozkySirky.resizerProps('obchodnik')} /></th>
                      <th>Id<span {...polozkySirky.resizerProps('id')} /></th>
                      <th>Médium<span {...polozkySirky.resizerProps('medium')} /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailBookingItems.map((item, idx) => {
                      const isSelected = selectedBookingItems.has(item.id);
                      return (
                        <tr
                          key={item.id || idx}
                          className={isSelected ? 'mpd-row-selected' : ''}
                          onClick={() => toggleBookingItemSelection(item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              onChange={() => toggleBookingItemSelection(item.id)}
                            />
                          </td>
                          <td>{item.company_name || '—'}</td>
                          <td className="text-nowrap">{formatDate(item.date)}</td>
                          <td>{item.booking_number || '—'}</td>
                          <td>{item.invoice_number || '—'}</td>
                          <td>
                            {item.internal_note ? (
                              <span title={item.internal_note}>
                                {item.internal_note.length > 60
                                  ? item.internal_note.substring(0, 60) + '…'
                                  : item.internal_note}
                              </span>
                            ) : '—'}
                          </td>
                          {/* Poznámka samotné položky objednávky (booking_item.note) —
                              interní poznámka vedle ní patří celé objednávce. */}
                          <td>
                            {item.item_note ? (
                              <span title={item.item_note}>
                                {item.item_note.length > 60
                                  ? item.item_note.substring(0, 60) + '…'
                                  : item.item_note}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            {item.ratecard_item_name || '—'}
                            {item.ratecard_format_label && (
                              <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>
                                ({item.ratecard_format_label})
                              </span>
                            )}
                          </td>
                          <td className="text-end text-nowrap">
                            {formatPrice(item.net_net_price, item.currency)}
                          </td>
                          <td className="text-nowrap">
                            {item.salesperson_name || '—'}
                            {item.entered_by_name && item.entered_by_name !== item.salesperson_name && (
                              <div className="text-muted" style={{ fontSize: '0.8rem' }} title="Objednávku zadal/a">
                                zadal/a: {item.entered_by_name}
                              </div>
                            )}
                          </td>
                          <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {item.number || '—'}
                          </td>
                          <td>{item.medium_name || '—'}</td>
                        </tr>
                      );
                    })}
                    {detailBookingItems.length === 0 && (
                      <tr>
                        <td colSpan="12" className="text-center text-muted py-4">
                          Žádné záznamy pro tento projekt
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedBookingItems.size > 0 && (
            <Button variant="primary" className="me-auto" onClick={handleOpenInvoiceModal}>
              <i className="bi bi-receipt me-1"></i>
              Vytvořit fakturu ({selectedBookingItems.size})
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => setDetailModalProject(null)}>
            Zavřít
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Invoice Modal — custom overlay to darken the project detail modal behind */}
      {showInvoiceModal && (
        <div className="mpd-invoice-overlay" onClick={() => !invoiceCreating && setShowInvoiceModal(false)}>
          <div className="mpd-invoice-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mpd-invoice-header">
              <h5 className="mb-0">
                <i className="bi bi-receipt me-2"></i>Vytvořit fakturu
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => !invoiceCreating && setShowInvoiceModal(false)}
                disabled={invoiceCreating}
              />
            </div>
            <div className="mpd-invoice-body">
              {invoiceTypesLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Načítání...</span>
                  </div>
                </div>
              ) : invoiceCreating ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Vytvářím faktury...</span>
                  </div>
                  <div className="text-muted">Vytvářím faktury, prosím čekejte...</div>
                </div>
              ) : (
                <>
                  {invoiceGroups.some(g => g.currency && g.currency !== 'CZK') && (
                    <div className="alert alert-warning py-2 small">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Mezi vybranými položkami je doklad v cizí měně. Tady se přepočítá
                      <strong> kurzem z objednávky</strong> — ten je z data, kdy objednávka vznikla.
                      Když chceš vybrat kurz (ČNB ke dni uskutečnění plnění, nebo vlastní),
                      vystav fakturu z detailu objednávky.
                    </div>
                  )}
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover align-middle mb-0 mpd-project-detail-table">
                      <thead className="table-light">
                        <tr>
                          <th>Společnost</th>
                          <th className="text-center">Počet položek</th>
                          <th className="text-end">Cena s DPH</th>
                          <th>Měna</th>
                          <th>Projekty</th>
                          <th>Datum vydání</th>
                          <th>Interní poznámka</th>
                          <th className="text-center" title="Vytvořit zvláštní fakturu pro každou položku objednávky">Po jedné</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceGroups.map((group, gIdx) => (
                          <tr key={gIdx}>
                            <td className="fw-semibold">{group.company_name}</td>
                            <td className="text-center">{group.items.length}</td>
                            <td className="text-end text-nowrap">
                              {formatPrice(group.total_net, group.currency)}
                            </td>
                            <td>{group.currency || '—'}</td>
                            <td>{group.project_names.join(', ') || detailModalProject?.name || '—'}</td>
                            <td className="text-nowrap">
                              {group.issue_dates.length > 0
                                ? group.issue_dates.map(d => formatDate(d)).join(', ')
                                : '—'}
                            </td>
                            <td>
                              {group.internal_notes.length > 0 ? (
                                group.internal_notes.map((note, nIdx) => {
                                  const truncated = note.length > 40 ? note.substring(0, 40) + '…' : note;
                                  return (
                                    <span
                                      key={nIdx}
                                      title={note}
                                      className="d-block text-muted"
                                      style={{ fontSize: '0.82rem', cursor: note.length > 40 ? 'pointer' : 'default' }}
                                    >
                                      {truncated}
                                    </span>
                                  );
                                })
                              ) : '—'}
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={separateCompanies.has(group.company_name)}
                                onChange={() => toggleSeparateCompany(group.company_name)}
                                title="Vytvořit zvláštní fakturu pro každou položku objednávky"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Typ dokladu</label>
                    <select
                      className="form-select"
                      value={selectedInvoiceType}
                      onChange={(e) => setSelectedInvoiceType(e.target.value)}
                    >
                      {invoiceTypes.map(t => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="mpd-invoice-footer">
              <Button
                variant="outline-secondary"
                onClick={() => setShowInvoiceModal(false)}
                disabled={invoiceCreating}
              >
                Zrušit
              </Button>
              <Button
                variant="primary"
                disabled={invoiceTypesLoading || invoiceCreating || !selectedInvoiceType}
                onClick={handleCreateInvoices}
              >
                {invoiceCreating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Vytvářím...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Dokončit
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaPricelistDetail;
